// src/components/fiscal/NfseConfigModal.jsx
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { FaTimes, FaSave, FaTrash } from 'react-icons/fa';
import { useToast } from './ToastContext';
import config from './../config/config';

// Configurações padrão
const DEFAULT_CONFIG = {
  codigoMunicipioIbge: '',
  inscricaoMunicipal: '',
  cpfPrestadorAutonomo: '',
  // '1' (nao ''0'') = "Nao Optante" - opSimpNac (TSOpSimpNac) so aceita "1"
  // (nao optante), "2" (optante MEI) ou "3" (optante ME/EPP) no XSD do
  // Sistema Nacional NFS-e, "0" nao existe na enumeracao e o Cidade360/
  // SEFIN rejeitam a DPS com P9020/P9021 ("nao e um valor valido"). Bug real
  // em producao (31/08/2026): o <Select> abaixo tinha so 2 opcoes ("Nao"
  // value="0" / "Sim" value="1") e esse default '0' ficava salvo tal e qual
  // em EmpresaNfseConfig.opSimpNac e ia pro XML - diferente de regApTribSN
  // (abaixo), esse campo e obrigatorio no schema, entao nao da pra sair do
  // bug so trocando o default por '' (backend rejeita com "opSimpNac e
  // obrigatorio" antes mesmo de tentar enviar).
  opSimpNac: '1',
  // '' (nao ''0'') = "nao se aplica"/omitir - regApTribSN (TSRegimeApuracaoSimpNac)
  // so aceita "1"/"2"/"3" no XSD do Sistema Nacional NFS-e, "0" nao existe na
  // enumeracao e o Cidade360/SEFIN rejeitam a DPS com P9020/P9021 ("nao e um
  // valor valido"). Bug real em QA (10/08/2026): esse default '0' ficava
  // salvo tal e qual em EmpresaNfseConfig.regApTribSN e ia pro XML.
  regApTribSN: '',
  regEspTrib: '',
  ambiente: '1',
  serieDps: '1',
  proximoNumeroDps: 1,
  tipoProvedorNfse: 'NACIONAL',
  cidade360UrlProducao: '',
  cidade360UrlHomologacao: '',
  codigoTribNacPadrao: '',
  codigoTribMunPadrao: '',
  descricaoServicoPadrao: '',
  // tribIssqnPadrao (TSTribISSQN) e tpRetIssqnPadrao (TSTipoRetISSQN) sao
  // campos OBRIGATORIOS no schema do Sistema Nacional NFS-e (nao tem "nao se
  // aplica"/omitir, ao contrario de regApTribSN acima) - mesma classe de bug
  // do opSimpNac/regApTribSN documentada acima: os <Select> abaixo tinham
  // "0" como opcao normal ("Nao Tributado"/"Nao"), mas "0" NAO existe na
  // enumeracao real (TSTribISSQN so aceita 1/2/3/4, TSTipoRetISSQN so aceita
  // 1/2/3 - ver DanfseService.descreverTribIssqn/descreverTpRetIssqn). Erro
  // real em producao (17/09/2026): E1235 "The value '0' is invalid
  // according to its datatype ... Enumeration constraint failed" ao tentar
  // emitir pelo SEFIN Nacional. Default trocado pro valor mais comum (1 =
  // Operacao Tributavel / Nao Retido), nunca mais '0'.
  tribIssqnPadrao: '1',
  tpRetIssqnPadrao: '1',
  codigoNbsPadrao: '',
  codigoIntContribuintePadrao: '',
  // Grupo IBSCBS (Reforma Tributaria) - obrigatorio na DPS do Cidade360
  // desde 01/10/2026 (ATO CONJUNTO RFB/CGIBS no 4/2026) para o item de
  // servico desta empresa. Sem default: NfseCidade360Service recusa a
  // emissao com erro explicito em vez de adivinhar um valor.
  cIndOpPadrao: '',
  cstIbsCbsPadrao: '',
  classTribIbsCbsPadrao: '',
};

const NfseConfigModal = ({ isOpen, onClose, onConfigUpdated }) => {
  const { addToast } = useToast();
  const { API_BASE_URL } = config;
  const NFSE_CONFIG_URL = '/empresas/nfse-config';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [configData, setConfigData] = useState(DEFAULT_CONFIG);
  const [hasExistingConfig, setHasExistingConfig] = useState(false);

  // Funções do serviço usando axios diretamente
  const nfseConfigService = {
    getConfig: async () => {
      try {
        const token = localStorage.getItem("jwtToken");
        const response = await axios.get(`${API_BASE_URL}${NFSE_CONFIG_URL}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },

    saveConfig: async (dados) => {
      const token = localStorage.getItem("jwtToken");
      const response = await axios.put(`${API_BASE_URL}${NFSE_CONFIG_URL}`, dados, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return response.data;
    },

    deleteConfig: async () => {
      const token = localStorage.getItem("jwtToken");
      await axios.delete(`${API_BASE_URL}${NFSE_CONFIG_URL}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
    }
  };

  // Carregar configuração existente
  useEffect(() => {
    if (isOpen) {
      loadConfig();
    }
  }, [isOpen]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const data = await nfseConfigService.getConfig();
      if (data) {
        setConfigData({
          ...DEFAULT_CONFIG,
          ...data,
          proximoNumeroDps: data.proximoNumeroDps || 1,
          // Empresas que salvaram config com o bug do '0' antigo (ver
          // DEFAULT_CONFIG.regApTribSN) tem esse lixo gravado no banco -
          // sanitiza ao carregar em vez de deixar o Select sem opção
          // correspondente e o usuário salvar o '0' de volta sem querer.
          regApTribSN: ['1', '2', '3'].includes(String(data.regApTribSN)) ? String(data.regApTribSN) : '',
        });
        setHasExistingConfig(true);
      } else {
        setConfigData(DEFAULT_CONFIG);
        setHasExistingConfig(false);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.href = "/login";
      } else {
        addToast('error', 'Erro ao carregar configuração fiscal.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Salvar configuração
  const handleSave = async () => {
    // Validação básica
    if (!configData.codigoMunicipioIbge) {
      addToast('warning', 'O código do município IBGE é obrigatório.');
      return;
    }

    if (!configData.cpfPrestadorAutonomo && !configData.inscricaoMunicipal) {
      addToast('warning', 'Informe o CPF (autônomo) ou a inscrição municipal.');
      return;
    }

    // Validar URLs do Cidade360 se for o provedor selecionado
    if (configData.tipoProvedorNfse === 'CIDADE360') {
      if (!configData.cidade360UrlProducao && configData.ambiente === '1') {
        addToast('warning', 'URL de produção do Cidade360 é obrigatória.');
        return;
      }
      if (!configData.cidade360UrlHomologacao && configData.ambiente === '2') {
        addToast('warning', 'URL de homologação do Cidade360 é obrigatória.');
        return;
      }
    }

    setSaving(true);
    try {
      const dataToSave = {
        ...configData,
        proximoNumeroDps: Number(configData.proximoNumeroDps),
      };
      
      const saved = await nfseConfigService.saveConfig(dataToSave);
      setConfigData(saved);
      setHasExistingConfig(true);
      addToast('success', 'Configuração fiscal salva com sucesso!');
      
      if (onConfigUpdated) {
        onConfigUpdated(saved);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.href = "/login";
      } else {
        addToast('error', 'Erro ao salvar configuração fiscal.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Deletar configuração
  const handleDelete = async () => {
    if (!window.confirm('Tem certeza que deseja excluir a configuração fiscal? Isso pode afetar a emissão de NFS-e.')) {
      return;
    }

    setSaving(true);
    try {
      await nfseConfigService.deleteConfig();
      setConfigData(DEFAULT_CONFIG);
      setHasExistingConfig(false);
      addToast('success', 'Configuração fiscal removida.');
      if (onConfigUpdated) {
        onConfigUpdated(null);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        window.location.href = "/login";
      } else {
        addToast('error', 'Erro ao excluir configuração fiscal.');
      }
    } finally {
      setSaving(false);
    }
  };

  // Handler para mudanças nos campos
  const handleChange = (field, value) => {
    setConfigData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHeaderBar>
          <div>
            <h3>Configurações Fiscais - NFS-e</h3>
            <ModalSubtitle>Configure os dados para emissão de NFS-e</ModalSubtitle>
          </div>
          <CloseX onClick={onClose}><FaTimes /></CloseX>
        </ModalHeaderBar>

        <ModalBody>
          {loading ? (
            <LoadingState>Carregando configurações...</LoadingState>
          ) : (
            <>
              {/* Seção: Dados do Prestador */}
              <Section>
                <SectionTitle>Dados do Prestador</SectionTitle>
                <FormGrid>
                  <FormGroup>
                    <Label>Código Município IBGE *</Label>
                    <Input
                      type="text"
                      value={configData.codigoMunicipioIbge}
                      onChange={(e) => handleChange('codigoMunicipioIbge', e.target.value)}
                      placeholder="Ex: 4314902"
                    />
                    <HelperText>Código IBGE do município do prestador</HelperText>
                  </FormGroup>

                  <FormGroup>
                    <Label>Inscrição Municipal</Label>
                    <Input
                      type="text"
                      value={configData.inscricaoMunicipal}
                      onChange={(e) => handleChange('inscricaoMunicipal', e.target.value)}
                      placeholder="Ex: 123456"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>CPF (Prestador Autônomo)</Label>
                    <Input
                      type="text"
                      value={configData.cpfPrestadorAutonomo}
                      onChange={(e) => handleChange('cpfPrestadorAutonomo', e.target.value)}
                      placeholder="Ex: 123.456.789-00"
                    />
                    <HelperText>Preencher se for prestador autônomo (PF)</HelperText>
                  </FormGroup>
                </FormGrid>
              </Section>

              {/* Seção: Regime Tributário */}
              <Section>
                <SectionTitle>Regime Tributário</SectionTitle>
                <FormGrid>
                  <FormGroup>
                    <Label>Optante pelo Simples Nacional</Label>
                    <Select
                      value={configData.opSimpNac}
                      onChange={(e) => handleChange('opSimpNac', e.target.value)}
                    >
                      <option value="1">Não Optante</option>
                      <option value="2">Optante - MEI</option>
                      <option value="3">Optante - ME/EPP</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Regime de Apuração SN</Label>
                    <Select
                      value={configData.regApTribSN}
                      onChange={(e) => handleChange('regApTribSN', e.target.value)}
                    >
                      <option value="">Não se aplica</option>
                      <option value="1">Federais e municipal pelo Simples Nacional</option>
                      <option value="2">Federais pelo Simples Nacional, ISSQN por fora</option>
                      <option value="3">Federais e municipal por fora do Simples Nacional</option>
                    </Select>
                    <HelperText>Só se aplica a optante ME/EPP que ultrapassou algum limite do Simples Nacional</HelperText>
                  </FormGroup>

                  <FormGroup>
                    <Label>Regime Especial de Tributação</Label>
                    <Input
                      type="text"
                      value={configData.regEspTrib}
                      onChange={(e) => handleChange('regEspTrib', e.target.value)}
                      placeholder="Ex: RE-001"
                    />
                    <HelperText>Apenas se aplicável</HelperText>
                  </FormGroup>
                </FormGrid>
              </Section>

              {/* Seção: Configuração de Emissão */}
              <Section>
                <SectionTitle>Configuração de Emissão</SectionTitle>
                <FormGrid>
                  <FormGroup>
                    <Label>Ambiente</Label>
                    <Select
                      value={configData.ambiente}
                      onChange={(e) => handleChange('ambiente', e.target.value)}
                    >
                      <option value="1">Produção</option>
                      <option value="2">Homologação</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Série DPS</Label>
                    <Input
                      type="text"
                      value={configData.serieDps}
                      onChange={(e) => handleChange('serieDps', e.target.value)}
                      placeholder="Ex: 1"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Próximo Número DPS</Label>
                    <Input
                      type="number"
                      value={configData.proximoNumeroDps}
                      onChange={(e) => handleChange('proximoNumeroDps', Number(e.target.value))}
                      min="1"
                    />
                    <HelperText>Número sequencial da próxima DPS</HelperText>
                  </FormGroup>
                </FormGrid>
              </Section>

              {/* Seção: Provedor */}
              <Section>
                <SectionTitle>Provedor NFS-e</SectionTitle>
                <FormGrid>
                  <FormGroup fullWidth>
                    <Label>Tipo de Provedor</Label>
                    <Select
                      value={configData.tipoProvedorNfse}
                      onChange={(e) => handleChange('tipoProvedorNfse', e.target.value)}
                    >
                      <option value="NACIONAL">Nacional (Padrão)</option>
                      <option value="CIDADE360">Cidade360</option>
                    </Select>
                  </FormGroup>

                  {configData.tipoProvedorNfse === 'CIDADE360' && (
                    <>
                      <FormGroup fullWidth>
                        <Label>URL Produção</Label>
                        <Input
                          type="text"
                          value={configData.cidade360UrlProducao}
                          onChange={(e) => handleChange('cidade360UrlProducao', e.target.value)}
                          placeholder="Ex: https://nfse.cidade360.cloud"
                        />
                      </FormGroup>

                      <FormGroup fullWidth>
                        <Label>URL Homologação</Label>
                        <Input
                          type="text"
                          value={configData.cidade360UrlHomologacao}
                          onChange={(e) => handleChange('cidade360UrlHomologacao', e.target.value)}
                          placeholder="Ex: https://homologacao.cidade360.cloud"
                        />
                      </FormGroup>
                    </>
                  )}
                </FormGrid>
              </Section>

              {/* Seção: Tributação Padrão */}
              <Section>
                <SectionTitle>Tributação Padrão do Serviço</SectionTitle>
                <FormGrid>
                  <FormGroup>
                    <Label>Código Tributação Nacional</Label>
                    <Input
                      type="text"
                      value={configData.codigoTribNacPadrao}
                      onChange={(e) => handleChange('codigoTribNacPadrao', e.target.value)}
                      placeholder="Ex: 0101"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Código Tributação Municipal</Label>
                    <Input
                      type="text"
                      value={configData.codigoTribMunPadrao}
                      onChange={(e) => handleChange('codigoTribMunPadrao', e.target.value)}
                      placeholder="Ex: 101"
                    />
                  </FormGroup>

                  <FormGroup fullWidth>
                    <Label>Descrição do Serviço</Label>
                    <Input
                      type="text"
                      value={configData.descricaoServicoPadrao}
                      onChange={(e) => handleChange('descricaoServicoPadrao', e.target.value)}
                      placeholder="Descrição padrão do serviço"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>Tributação ISSQN</Label>
                    <Select
                      value={configData.tribIssqnPadrao}
                      onChange={(e) => handleChange('tribIssqnPadrao', e.target.value)}
                    >
                      <option value="1">Operação Tributável</option>
                      <option value="2">Imunidade</option>
                      <option value="3">Exportação de Serviço</option>
                      <option value="4">Não Incidência</option>
                    </Select>
                  </FormGroup>

                  <FormGroup>
                    <Label>Retenção ISSQN</Label>
                    <Select
                      value={configData.tpRetIssqnPadrao}
                      onChange={(e) => handleChange('tpRetIssqnPadrao', e.target.value)}
                    >
                      <option value="1">Não Retido</option>
                      <option value="2">Retido pelo Tomador</option>
                      <option value="3">Retido pelo Intermediário</option>
                    </Select>
                  </FormGroup>
                </FormGrid>
              </Section>

              {/* Seção: Cidade360 Específicos */}
              {configData.tipoProvedorNfse === 'CIDADE360' && (
                <Section>
                  <SectionTitle>Configurações Cidade360</SectionTitle>
                  <FormGrid>
                    <FormGroup>
                      <Label>Código NBS</Label>
                      <Input
                        type="text"
                        value={configData.codigoNbsPadrao}
                        onChange={(e) => handleChange('codigoNbsPadrao', e.target.value)}
                        placeholder="Código da Nomenclatura Brasileira de Serviços"
                      />
                      <HelperText>Classificação nacional do MDIC/Siscoserv</HelperText>
                    </FormGroup>

                    <FormGroup>
                      <Label>Código Int. Contribuinte</Label>
                      <Input
                        type="text"
                        value={configData.codigoIntContribuintePadrao}
                        onChange={(e) => handleChange('codigoIntContribuintePadrao', e.target.value)}
                        placeholder="Código municipal do contribuinte"
                      />
                      <HelperText>Usado pelo Cidade360 para desambiguar serviços</HelperText>
                    </FormGroup>

                    <FormGroup>
                      <Label>Código Indicador da Operação (cIndOp)</Label>
                      <Input
                        type="text"
                        value={configData.cIndOpPadrao}
                        onChange={(e) => handleChange('cIndOpPadrao', e.target.value)}
                        placeholder="Ex: 100301"
                      />
                      <HelperText>Grupo IBSCBS (Reforma Tributária) - obrigatório desde 01/10/2026. Consultar AnexoVIII-CorrelacaoItemNBSIndOpCClassTrib_IBSCBS no portal da NFS-e para o item de serviço desta empresa.</HelperText>
                    </FormGroup>

                    <FormGroup>
                      <Label>CST IBS/CBS</Label>
                      <Input
                        type="text"
                        value={configData.cstIbsCbsPadrao}
                        onChange={(e) => handleChange('cstIbsCbsPadrao', e.target.value)}
                        placeholder="Ex: 000"
                      />
                      <HelperText>Código de Situação Tributária do IBS/CBS</HelperText>
                    </FormGroup>

                    <FormGroup>
                      <Label>Classificação Tributária (cClassTrib)</Label>
                      <Input
                        type="text"
                        value={configData.classTribIbsCbsPadrao}
                        onChange={(e) => handleChange('classTribIbsCbsPadrao', e.target.value)}
                        placeholder="Ex: 000001"
                      />
                      <HelperText>Código de Classificação Tributária do IBS/CBS</HelperText>
                    </FormGroup>
                  </FormGrid>
                </Section>
              )}

              {/* Ações do Formulário */}
              <ActionRow>
                {hasExistingConfig && (
                  <DeleteButton onClick={handleDelete} disabled={saving}>
                    <FaTrash size={14} /> Excluir Configuração
                  </DeleteButton>
                )}
                <ButtonsRight>
                  <CancelButton onClick={onClose}>Cancelar</CancelButton>
                  <SaveButton onClick={handleSave} disabled={saving}>
                    {saving ? 'Salvando...' : <><FaSave size={14} /> Salvar Configuração</>}
                  </SaveButton>
                </ButtonsRight>
              </ActionRow>
            </>
          )}
        </ModalBody>
      </ModalBox>
    </ModalOverlay>
  );
};

// ─── STYLES ──────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalBox = styled.div`
  background: #fff;
  border-radius: 14px;
  width: 100%;
  max-width: 750px;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.25);
`;

const ModalHeaderBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #008080 0%, #20b2aa 100%);
  color: #fff;
  border-radius: 14px 14px 0 0;
  position: sticky;
  top: 0;
  z-index: 10;
  
  h3 {
    margin: 0;
    font-size: 1.1rem;
  }
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0 0;
  font-size: 0.78rem;
  opacity: 0.85;
`;

const CloseX = styled.button`
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  &:hover { background: rgba(255,255,255,0.3); }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px;
  color: #7f8c8d;
`;

const Section = styled.div`
  margin-bottom: 28px;
  
  &:last-of-type {
    margin-bottom: 20px;
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  color: #2c3e50;
  font-size: 0.9rem;
  font-weight: 700;
  padding-bottom: 8px;
  border-bottom: 2px solid #eef3f3;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  ${props => props.fullWidth && `
    grid-column: 1 / -1;
  `}
`;

const Label = styled.label`
  font-size: 0.78rem;
  font-weight: 600;
  color: #2c3e50;
`;

const Input = styled.input`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1.5px solid #e0e8e8;
  font-size: 0.85rem;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #008080;
  }
  
  &:disabled {
    background: #f5f7f7;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1.5px solid #e0e8e8;
  font-size: 0.85rem;
  background: #fff;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #008080;
  }
  
  &:disabled {
    background: #f5f7f7;
    cursor: not-allowed;
  }
`;

const HelperText = styled.small`
  font-size: 0.7rem;
  color: #95a5a6;
  margin-top: 2px;
`;

const ActionRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 16px;
  border-top: 1px solid #eef3f3;
  flex-wrap: wrap;
  gap: 12px;
`;

const ButtonsRight = styled.div`
  display: flex;
  gap: 10px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: #f1f5f5;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  color: #2c3e50;
  
  &:hover {
    background: #e2ebeb;
  }
`;

const SaveButton = styled.button`
  padding: 10px 24px;
  background: linear-gradient(135deg, #008080 0%, #20b2aa 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(0, 128, 128, 0.35);
  }
`;

const DeleteButton = styled.button`
  padding: 10px 20px;
  background: #fee;
  color: #e74c3c;
  border: 1.5px solid #e74c3c;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: ${props => props.disabled ? 0.6 : 1};
  
  &:hover:not(:disabled) {
    background: #e74c3c;
    color: #fff;
  }
`;

export default NfseConfigModal;